/**
 * classSeries/{id} — recurring weekly class times that auto-fill the calendar.
 *
 * A series says "Silver, Mondays 6:00-7:00pm in Studio A, this term" and the
 * generator turns it into one reservation per matching weekday across the
 * term, skipping any day the studio is closed.
 *
 * We deliberately DON'T write our own booking transaction. Each generated
 * session goes through the same useCreateReservation() slot-lock transaction
 * the rest of the calendar uses, so two classes can never share a room at the
 * same time, by construction. A session that's already booked (re-running the
 * generator, or a clash with another class) throws "already booked", which we
 * count as a conflict and skip. That makes re-running naturally idempotent.
 *
 * Times are 'HH:MM' 24-hour strings on 30-min boundaries; term bounds are
 * 'YYYY-MM-DD' (same all-day, timezone-free convention as closures).
 *
 * Rules: active staff read; Director writes. See shared/firestore.rules.
 * A Manager adds, moves and removes weekly times through the server lane
 * (netlify/functions/lib/classSeriesWrite.mjs, bug B9) instead.
 */

import { cancelledDayFromError, cancelledDayKey, isCancellationActive, type CancellationStateInput } from './cancelledAttendance.ts';
import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  Timestamp as FirestoreTimestamp,
  where,
  type FieldValue,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { useAuth } from './auth-context';
// The slot math comes from its own small file and the booking library loads
// on the first cancel, so neither is on the startup path (speed plan
// 2026-10-19, P2). Same functions the booking transaction itself uses.
import { slotDocId, slotKeysFor } from './reservationSlots';
import type { NewReservationInput } from './reservations';
import {
  createClassSeriesViaServer,
  deleteClassSeriesViaServer,
  releasedIdsFromError,
} from './offeringWriteClient';
import { closureForDate, type ClosureDoc } from './closures';
import {
  lettersMissingFromCalendar,
  type ABWeekCalendarLookup,
  weekPatternForDateKey,
} from './abWeekCalendar';
import { tsToMillis } from './tonight';
import {
  SCHEDULE_EDIT_SHRINK_TOLERANCE,
  scheduleEditShrinkMessage,
} from './scheduleEditShrink';
const cancelReservation = async (reservationId: string): Promise<void> => {
  await (await import('./reservations')).cancelReservation(reservationId);
};

export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type ClassSeriesWeekPattern = '' | 'A' | 'B';

export const CLASS_SERIES_WEEK_PATTERN_LABELS: Record<ClassSeriesWeekPattern, string> = {
  '': 'Every week',
  A: 'A weeks only',
  B: 'B weeks only',
};

const CLASS_SERIES_WEEK_PATTERNS: ClassSeriesWeekPattern[] = ['', 'A', 'B'];

export function normalizeClassSeriesWeekPattern(value: unknown): ClassSeriesWeekPattern {
  return typeof value === 'string' && CLASS_SERIES_WEEK_PATTERNS.includes(value as ClassSeriesWeekPattern)
    ? (value as ClassSeriesWeekPattern)
    : '';
}

export function classSeriesWeekPatternLabel(value: unknown): string {
  return CLASS_SERIES_WEEK_PATTERN_LABELS[normalizeClassSeriesWeekPattern(value)];
}

export interface ClassSeriesDoc {
  id: string;
  offeringId: string;
  roomId: string;
  /** 0 = Sunday … 6 = Saturday (matches JS Date.getDay()). */
  weekday: number;
  /** 'HH:MM' 24-hour, 30-min aligned. */
  startTime: string;
  /** 'HH:MM' 24-hour, 30-min aligned. Must be after startTime. */
  endTime: string;
  /** Inclusive term bounds, 'YYYY-MM-DD'. */
  termStart: string;
  termEnd: string;
  weekPattern?: ClassSeriesWeekPattern;
  /** Marks a comp-dance rehearsal block as required for families/staff. */
  isMandatory?: boolean;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export interface UseClassSeriesResult {
  loading: boolean;
  series: ClassSeriesDoc[];
  error: string | null;
  /** True until Firestore confirms this listener from the server. */
  fromCache: boolean;
}

/**
 * Subscribe to every recurring class or comp-dance time in the studio.
 *
 * Reads are active-staff-only under the rules, so a caller that can render
 * for a family account (the /teach schedule list) must pass its staff flag
 * here or every family visit fires a listener the rules are guaranteed to
 * deny. Disabled returns an empty list with loading false, same as every
 * other gated hook in this app.
 */
export function useAllClassSeries(enabled = true): UseClassSeriesResult {
  const [series, setSeries] = useState<ClassSeriesDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!db || !enabled) {
      setSeries([]);
      setLoading(false);
      setFromCache(false);
      return;
    }
    setLoading(true);
    setFromCache(true);
    const q = query(
      collection(db, 'classSeries'),
      orderBy('weekday'),
      orderBy('startTime')
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        setSeries(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<ClassSeriesDoc, 'id'>),
          }))
        );
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled]);

  return { loading, series, error, fromCache };
}
/**
 * Subscribe to every recurring time for one class, ordered by weekday then
 * start time so the list reads naturally (Mondays before Tuesdays, earlier
 * before later).
 */
export function useClassSeriesForOffering(
  offeringId: string | undefined
): UseClassSeriesResult {
  const [series, setSeries] = useState<ClassSeriesDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!db || !offeringId) {
      setSeries([]);
      setLoading(false);
      setFromCache(false);
      return;
    }
    setLoading(true);
    setFromCache(true);
    const q = query(
      collection(db, 'classSeries'),
      where('offeringId', '==', offeringId),
      orderBy('weekday'),
      orderBy('startTime')
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        setSeries(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<ClassSeriesDoc, 'id'>),
          }))
        );
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [offeringId]);

  return { loading, series, error, fromCache };
}

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * 'HH:MM' iff it's a valid 24-hour time on a quarter hour.
 *
 * Quarter hours, not half hours: the printed studio schedule really does have
 * classes at :15 and :45 (Red Tap/Ballet runs 4:15 to 5:00), and rejecting
 * them meant the real schedule could not be put on the calendar.
 */
function isHalfHourTime(t: string): boolean {
  const m = /^(\d{2}):(\d{2})$/.exec(t);
  if (!m) return false;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return hh >= 0 && hh <= 23 && mm % 15 === 0;
}

/** Minutes-since-midnight for an 'HH:MM' string (assumes validated). */
function timeToMinutes(t: string): number {
  const [hh, mm] = t.split(':').map(Number);
  return hh * 60 + mm;
}

/** 'YYYY-MM-DD' for a local Date. */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

export interface NewClassSeriesInput {
  offeringId: string;
  roomId: string;
  weekday: number;
  startTime: string;
  endTime: string;
  termStart: string;
  termEnd: string;
  weekPattern?: ClassSeriesWeekPattern;
  isMandatory?: boolean;
}

/**
 * The schedule-shape checks a new recurring time must pass before ANYTHING
 * is written: room, weekday, times, and term dates. Pulled out to a pure,
 * synchronous function so it can run TWICE with the exact same messages -
 * once here inside useCreateClassSeries, and once as a pre-submit gate on
 * the class-CREATE flow (Offerings.tsx), which needs to refuse an invalid
 * schedule BEFORE the offering itself is created, not after.
 *
 * Bobbie / Fable adversarial review, 2026-08-30 (PR #792): with the
 * create-flow's optional schedule section, createOffering used to commit
 * FIRST and only then hit these same checks inside useCreateClassSeries,
 * leaving a Director with an unintended identity-only class plus a failure
 * notice for something that should have blocked the submit outright. This
 * function is the one source of truth both callers share, so there is no
 * second copy of these rules (or these words) to drift.
 *
 * Deliberately EXCLUDES two checks that only make sense once the offering
 * itself exists: `offeringId` (useCreateClassSeries checks that separately)
 * and the exact-duplicate lookup (needs Firestore, and a brand-new class
 * cannot already have a duplicate of anything).
 */
export function validateClassSeriesSchedule(
  input: Pick<NewClassSeriesInput, 'roomId' | 'weekday' | 'startTime' | 'endTime' | 'termStart' | 'termEnd'>
): void {
  if (!input.roomId) throw new Error('Pick a room.');
  if (
    !Number.isInteger(input.weekday) ||
    input.weekday < 0 ||
    input.weekday > 6
  ) {
    throw new Error('Pick a day of the week.');
  }
  if (!isHalfHourTime(input.startTime) || !isHalfHourTime(input.endTime)) {
    throw new Error('Times must land on a quarter hour (:00, :15, :30, or :45).');
  }
  if (timeToMinutes(input.endTime) <= timeToMinutes(input.startTime)) {
    throw new Error('The end time must be after the start time.');
  }
  if (!input.termStart) throw new Error('Pick a term start date.');
  if (!input.termEnd) throw new Error('Pick a term end date.');
  if (input.termEnd < input.termStart) {
    throw new Error('The term end is before the term start.');
  }
}

/**
 * True when this class already has an EXACT duplicate of the requested time
 * (same room, weekday, start, end, week pattern, and term). Two DIFFERENT
 * meeting times are legal and common (a class that meets twice a week) - this
 * only refuses re-adding the identical slot. Existing series for the class
 * are fetched with a single offeringId filter (cheap, no composite index
 * needed) and compared client-side, since a class realistically has a
 * handful of recurring times, never enough to make that scan expensive.
 */
async function findExactDuplicateClassSeries(
  firestore: Firestore,
  input: NewClassSeriesInput,
  weekPattern: ClassSeriesWeekPattern
): Promise<boolean> {
  const snap = await getDocs(
    query(collection(firestore, 'classSeries'), where('offeringId', '==', input.offeringId))
  );
  return snap.docs.some((d) => {
    const data = d.data() as Partial<ClassSeriesDoc>;
    return (
      data.roomId === input.roomId &&
      data.weekday === input.weekday &&
      data.startTime === input.startTime &&
      data.endTime === input.endTime &&
      normalizeClassSeriesWeekPattern(data.weekPattern) === weekPattern &&
      data.termStart === input.termStart &&
      data.termEnd === input.termEnd
    );
  });
}

/**
 * Hook returning a create function bound to the signed-in Director or
 * Manager. `viaServer` is the Manager's door (bug B9): classSeries create is
 * Director-only in the client rules, so a Manager's new weekly time goes
 * through the offeringWrite Function instead. Same validation, same
 * duplicate check, same document either way.
 */
export function useCreateClassSeries(options: { viaServer?: boolean } = {}) {
  const { user } = useAuth();
  const viaServer = options.viaServer === true;
  return async (input: NewClassSeriesInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const weekPattern = normalizeClassSeriesWeekPattern(input.weekPattern);
    if (!input.offeringId) throw new Error('Pick a class.');
    validateClassSeriesSchedule(input);
    if (await findExactDuplicateClassSeries(db, input, weekPattern)) {
      throw new Error(
        'This class already has a recurring time at that room, day, and hour. ' +
          'Pick a different day, time, or repeat pattern, or remove the existing one first.'
      );
    }
    if (viaServer) {
      return createClassSeriesViaServer(user, {
        offeringId: input.offeringId,
        roomId: input.roomId,
        weekday: input.weekday,
        weekPattern,
        isMandatory: Boolean(input.isMandatory),
        startTime: input.startTime,
        endTime: input.endTime,
        termStart: input.termStart,
        termEnd: input.termEnd,
      });
    }
    const ref = await addDoc(collection(db, 'classSeries'), {
      offeringId: input.offeringId,
      roomId: input.roomId,
      weekday: input.weekday,
      weekPattern,
      isMandatory: Boolean(input.isMandatory),
      startTime: input.startTime,
      endTime: input.endTime,
      termStart: input.termStart,
      termEnd: input.termEnd,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
    });
    return ref.id;
  };
}

/**
 * Push a recurring time's term end LATER, and never earlier.
 *
 * Bobbie, 2026-08-27: "the rec classes should go until the end of May".
 *
 * Until this, classSeries had create and delete and nothing between, so
 * lengthening a term meant deleting the recurring time and retyping it -
 * which cancels every future booking it made on the way past. This changes
 * the one field, so the bookings that already exist stay exactly where they
 * are and the term-fill booker adds the new weeks on top.
 *
 * The Firestore rule for this was already open: a Director may update a
 * classSeries when the shape holds and auditKept() preserves createdAt and
 * createdBy. Sending termEnd alone satisfies both - the merged doc keeps
 * every other field, the audit stamps included.
 *
 * REFUSES TO SHORTEN, on the server's own copy rather than on what a caller
 * believed. Pulling a term in would strand booked sessions past the new end
 * with no series behind them, and nobody asked for that; the extension sweep
 * filters for it too, and this is the floor under that filter. A term that
 * already runs that far is a no-op, not an error, so a re-run of the same
 * sweep costs nothing.
 */
export async function extendClassSeriesTerm(
  seriesId: string,
  termEnd: string
): Promise<'extended' | 'alreadyLongEnough'> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(termEnd)) throw new Error('Pick a term end date.');
  const ref = doc(db, 'classSeries', seriesId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That weekly time no longer exists.');
  const data = snap.data() as Partial<ClassSeriesDoc>;
  const current = typeof data.termEnd === 'string' ? data.termEnd : '';
  if (current && current >= termEnd) return 'alreadyLongEnough';
  if (typeof data.termStart === 'string' && termEnd < data.termStart) {
    throw new Error('That date is before the class starts.');
  }
  await updateDoc(ref, { termEnd });
  return 'extended';
}

/**
 * Pull a recurring time's term end BACK, and never later. Direction-aware
 * mirror of extendClassSeriesTerm above: that one refuses to shorten, this
 * one refuses to lengthen, and between the two nothing about a class's term
 * end can move without going through a function that checks its own
 * direction against the SERVER'S OWN COPY rather than what a caller believed.
 *
 * Bobbie, 2026-08-28, after the 1701-session run surfaced roughly two weeks
 * of June 2027 sessions: several rec class series still carried a stored
 * termEnd of 2027-06-12 from an earlier half-run era, and the never-shortens
 * rule on extendClassSeriesTerm correctly respected that as their own end.
 * Asked directly whether any of those classes run into June on purpose:
 * "They all end may 31st." See DECISIONS.md, 2026-08-28. planTermExtension's
 * runsPast group finds these; this is the write that fixes one.
 *
 * REFUSES TO LENGTHEN, on the stored value: a term already at or before the
 * target is a no-op, not an error, so a re-run of the trim after a partial
 * failure costs nothing and never accidentally pushes a term back OUT.
 *
 * ORDER OF OPERATIONS IS THE CALLER'S JOB, not this function's, and it is
 * load-bearing: the trim button in StudioSettings.tsx deletes every
 * out-of-term reservation for a series FIRST (one at a time, via
 * cancelReservation, so slot locks are released transactionally) and calls
 * this LAST. A run that dies partway through deleting sessions and never
 * reaches this write leaves termEnd exactly where it was - the class still
 * visibly carries bookings past the target and its runsPast row keeps
 * showing up next time, rather than reading as already fixed while its
 * calendar quietly disagrees with its own stored end.
 */
export async function trimClassSeriesTerm(
  seriesId: string,
  termEnd: string
): Promise<'trimmed' | 'alreadyThatShortOrShorter'> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(termEnd)) throw new Error('Pick a term end date.');
  const ref = doc(db, 'classSeries', seriesId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That weekly time no longer exists.');
  const data = snap.data() as Partial<ClassSeriesDoc>;
  const current = typeof data.termEnd === 'string' ? data.termEnd : '';
  if (current && current <= termEnd) return 'alreadyThatShortOrShorter';
  if (typeof data.termStart === 'string' && termEnd < data.termStart) {
    throw new Error('That date is before the class starts.');
  }
  await updateDoc(ref, { termEnd });
  return 'trimmed';
}

/** Local midnight at the start of whatever day this instant falls on. */
export function startOfLocalDay(when: Date): Date {
  return new Date(when.getFullYear(), when.getMonth(), when.getDate(), 0, 0, 0, 0);
}

/**
 * PURE. Which of the rows read back from that midnight query are actually
 * still ahead, and therefore safe to cancel.
 *
 * THE QUERY REACHES BACK FURTHER THAN THE ANSWER DOES, on purpose, and this
 * is the half that stops that being a bug. A row with no readable startAt is
 * LEFT ALONE rather than cancelled: this list feeds a cancel pass, so
 * guessing at an unreadable timestamp would release a booking nobody can
 * prove is in the future.
 */
export function futureReservationIdsFromRows(
  rows: readonly { id: string; startAt: unknown }[],
  nowMs: number
): string[] {
  const out: string[] = [];
  for (const row of rows) {
    const startMs = tsToMillis(row.startAt);
    if (startMs === null) continue;
    if (startMs <= nowMs) continue;
    out.push(row.id);
  }
  return out;
}

/**
 * Every booking of this series that is still ahead of us.
 *
 * ONE BOUNDARY, SHARED WITH THE A/B RECONCILE CARD (Calendar.tsx, #866), and
 * the comment this replaces got it wrong in a way worth naming. It said
 * `startAt >= now` was "the same boundary buildOccurrences() regenerates
 * from". It is not: buildOccurrences starts at localDateKey(fromDate), which
 * is the WHOLE of today. So the two passes were reading different windows
 * and only agreed by accident.
 *
 * The query now asks from local midnight, exactly as the reconcile card
 * does, and the "still ahead" job is done in code by
 * futureReservationIdsFromRows above rather than by the range filter. That
 * is the same split planClassReconcile uses (`row.startMs <= nowMs` there),
 * so a session that met earlier today is read by both lanes and cancelled by
 * neither.
 *
 * Shared by deleteClassSeries below and by the schedule edit further down so
 * the two can never disagree about which bookings belong to a series. It was
 * inline in deleteClassSeries until the edit flow needed the same answer.
 *
 * INDEX: reservations (seriesId ASC, startAt ASC), which is already in
 * shared/firestore.indexes.json - this is the same equality-plus-range query
 * deleteClassSeries has always run, just named. Checked by hand on
 * 2026-09-01 because the rules emulator does not enforce composite indexes
 * and a missing one only ever shows up against the real cloud.
 */
export async function futureReservationIdsForSeries(
  seriesId: string,
  now: Date = new Date()
): Promise<string[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const snap = await getDocs(query(
    collection(db, 'reservations'),
    where('seriesId', '==', seriesId),
    where('startAt', '>=', FirestoreTimestamp.fromDate(startOfLocalDay(now)))
  ));
  return futureReservationIdsFromRows(
    snap.docs.map((d) => ({ id: d.id, startAt: (d.data() as { startAt?: unknown }).startAt })),
    now.getTime()
  );
}

/**
 * The dates this series already has on the calendar from today forward.
 *
 * Bobbie, 2026-09-03: "it just went away and isn't on the calendar at all even
 * if it is showing up on the class card." The class page shows the RULE; this
 * is what lets it also show the TRUTH, without sending anybody to another
 * page to find out. Same query and same index as futureReservationIdsForSeries
 * above, returning local date keys instead of ids.
 *
 * Returns null rather than [] when the read fails, so the screen can say "not
 * checked" instead of the much worse "nothing is booked".
 */
export async function upcomingSessionDatesForSeries(
  seriesId: string,
  now: Date = new Date()
): Promise<string[] | null> {
  if (!db) return null;
  try {
    const snap = await getDocs(query(
      collection(db, 'reservations'),
      where('seriesId', '==', seriesId),
      where('startAt', '>=', FirestoreTimestamp.fromDate(startOfLocalDay(now)))
    ));
    const nowMs = now.getTime();
    const out: string[] = [];
    for (const d of snap.docs) {
      const startMs = tsToMillis((d.data() as { startAt?: unknown }).startAt);
      if (startMs === null || startMs <= nowMs) continue;
      out.push(localDateKey(new Date(startMs)));
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Which of these series ids STILL EXIST, asked of the server directly.
 *
 * Caught in review of the clear-leftovers button, and it is the difference
 * between a repair and data loss. That button decides a booking is a leftover
 * because its seriesId is not among the class's live weekly times, and the
 * live list comes from an onSnapshot listener that Firestore's persistent
 * cache can answer instantly with a stale copy. A cached list missing one of
 * the class's own live times would make that time's real, correct bookings
 * look like leftovers, and the very next thing the button does is delete them.
 *
 * A listener cannot be trusted for a decision that deletes. This asks the
 * server for each id and returns only the ones that genuinely resolve to a
 * live series doc. Returns null when the check itself fails, so the caller
 * refuses to delete rather than assuming the ids are dead.
 */
export async function existingSeriesIds(ids: readonly string[]): Promise<Set<string> | null> {
  if (!db) return null;
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return new Set();
  try {
    const firestore = db;
    const snaps = await Promise.all(
      unique.map((id) => getDocFromServer(doc(firestore, 'classSeries', id)))
    );
    const out = new Set<string>();
    for (const snap of snaps) {
      if (snap.exists()) out.add(snap.id);
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * A room lock with no booking behind it, holding a date hostage.
 *
 * THIS IS THE REAL SHAPE OF BOBBIE'S BUG, found 2026-09-03 after the
 * leftover-bookings theory was disproved by her own screen: she opened Studio
 * Settings and the leftover-bookings card - titled "Class leftovers to clean
 * up" today, and "Bookings whose weekly time no longer matches" on the day
 * this was written - was not there at all. That card is mounted and self-hides
 * when it finds nothing, so its absence PROVED no reservation with a dead
 * weekly time was involved.
 *
 * The room is locked by the `slots` collection, not by the reservations. Look
 * at the booking transaction (lib/reservations.ts, createReservation):
 *
 *     if (slotSnapshots.some((snap) => snap.exists())) {
 *       throw new Error('That room is already booked for part of that time.');
 *     }
 *
 * It asks ONLY whether a slot doc exists. It never asks whether a reservation
 * exists behind it. So a slot doc whose reservation is gone blocks that room
 * and hour forever, and is invisible everywhere a person would look: the
 * calendar draws reservations, so it shows nothing; the stale-bookings finder
 * scans reservations, so it finds nothing; and the class page could only say
 * "already taken".
 *
 * That is exactly Junior Hip Hop: seven B-week Mondays all "already taken",
 * zero sessions on the class, nothing on the calendar, and an empty repair
 * card. Every symptom at once, and no screen able to name it.
 *
 * Returns null when the read fails, never an empty array, so "could not look"
 * is never reported as "nothing is stuck".
 */
export interface OrphanedSlot {
  slotId: string;
  dateKey: string;
  /** The booking this lock claims to belong to, which no longer exists. */
  reservationId: string | null;
}

export async function orphanedSlotsForDates(
  series: Pick<ClassSeriesDoc, 'roomId' | 'startTime' | 'endTime'>,
  dateKeys: readonly string[]
): Promise<OrphanedSlot[] | null> {
  if (!db) return null;
  if (dateKeys.length === 0) return [];
  const firestore = db;
  const [startH, startM] = series.startTime.split(':').map(Number);
  const [endH, endM] = series.endTime.split(':').map(Number);
  const out: OrphanedSlot[] = [];
  try {
    for (const dateKey of dateKeys) {
      const [y, m, d] = parseDateKey(dateKey);
      const start = new Date(y, m - 1, d, startH, startM, 0, 0);
      const end = new Date(y, m - 1, d, endH, endM, 0, 0);
      const ids = slotKeysFor(start, end).map((k) => slotDocId(series.roomId, k));
      for (const slotId of ids) {
        // From the SERVER: this decides whether a lock gets released, and a
        // cached answer either way would be the wrong basis for that.
        const snap = await getDocFromServer(doc(firestore, 'slots', slotId));
        if (!snap.exists()) continue;
        const reservationId = (snap.data() as { reservationId?: unknown }).reservationId;
        const rid = typeof reservationId === 'string' ? reservationId : null;
        // A lock naming a booking that still exists is doing its job. Only a
        // lock whose booking is GONE (or that names none at all) is an orphan.
        if (rid) {
          const res = await getDocFromServer(doc(firestore, 'reservations', rid));
          if (res.exists()) continue;
        }
        out.push({ slotId, dateKey, reservationId: rid });
      }
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Release orphaned room locks. Returns how many were freed.
 *
 * Deliberately re-checks each one immediately before deleting, on the server,
 * so a lock that gained a real booking between the scan and the tap is left
 * alone. Deleting a live lock would let two classes book the same room.
 */
export interface ReleaseOrphanedSlotsResult {
  freed: number;
  /** Left alone because a real booking turned up behind the lock. */
  spared: number;
  /** The delete or the re-check errored. NOT the same fact as spared. */
  errored: number;
}

export async function releaseOrphanedSlots(
  slots: readonly OrphanedSlot[]
): Promise<ReleaseOrphanedSlotsResult> {
  if (!db) return { freed: 0, spared: 0, errored: slots.length };
  const firestore = db;
  let freed = 0;
  let spared = 0;
  let errored = 0;
  for (const slot of slots) {
    try {
      const snap = await getDocFromServer(doc(firestore, 'slots', slot.slotId));
      if (!snap.exists()) { freed += 1; continue; }
      const rid = (snap.data() as { reservationId?: unknown }).reservationId;
      if (typeof rid === 'string' && rid) {
        const res = await getDocFromServer(doc(firestore, 'reservations', rid));
        if (res.exists()) { spared += 1; continue; }
      }
      await deleteDoc(doc(firestore, 'slots', slot.slotId));
      freed += 1;
    } catch {
      // Caught in review: this used to be counted with the spared ones, so a
      // permission-denied or a dropped connection told a Director the room was
      // legitimately booked while her calendar showed nothing there. That is an
      // invented claim about her data. A lock with NO reservationId field lands
      // here for real: both arms of the delete rule dereference that field, so
      // a missing key is an evaluation error and the delete is refused.
      errored += 1;
    }
  }
  return { freed, spared, errored };
}

/** One booking standing in the way of a date this series wanted. */
export interface BookingBlocker {
  id: string;
  dateKey: string;
  offeringId: string | null;
  seriesId: string | null;
}

/**
 * WHAT is holding the dates this series could not book.
 *
 * Bobbie, 2026-09-03, on Junior Hip Hop, Mondays 9:00 PM in the Pink Room, B
 * weeks only: the page said "Not on the calendar", and underneath, "7 dates
 * were already taken". Every bookable B-week Monday she had was blocked, the
 * class owned no sessions at all, and there was NO WAY ON THE SCREEN to find
 * out by what. "Already taken" covers three situations that need three
 * different answers, and a count cannot tell them apart:
 *
 *   - this class's own booking, in which case nothing is wrong
 *   - a leftover of a weekly time that was REMOVED from this same class, which
 *     is what Remove + Add leaves behind and what has to be cleared
 *   - a genuinely different class in that room, which is a scheduling clash
 *     for a person to resolve, never for the app to bulldoze
 *
 * This reads the bookings sitting in the series' own room, at its own hours,
 * on exactly the dates that failed, so the page can say which. Returns null
 * (never an empty array) when the read fails, so a caller can tell "nothing is
 * holding these" apart from "could not look", and say so honestly.
 */
export async function blockersForDates(
  series: Pick<ClassSeriesDoc, 'roomId' | 'startTime' | 'endTime'>,
  dateKeys: readonly string[]
): Promise<BookingBlocker[] | null> {
  if (!db) return null;
  if (dateKeys.length === 0) return [];
  const sorted = [...dateKeys].sort();
  const [fy, fm, fd] = parseDateKey(sorted[0]);
  const [ly, lm, ld] = parseDateKey(sorted[sorted.length - 1]);
  const from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const to = new Date(ly, lm - 1, ld, 23, 59, 59, 999);
  const wanted = new Set(sorted);
  const [startH, startM] = series.startTime.split(':').map(Number);
  const [endH, endM] = series.endTime.split(':').map(Number);
  try {
    const snap = await getDocs(query(
      collection(db, 'reservations'),
      where('startAt', '>=', FirestoreTimestamp.fromDate(from)),
      where('startAt', '<=', FirestoreTimestamp.fromDate(to))
    ));
    const out: BookingBlocker[] = [];
    for (const d of snap.docs) {
      const data = d.data() as {
        roomId?: unknown; startAt?: unknown; endAt?: unknown;
        offeringId?: unknown; seriesId?: unknown;
      };
      if (data.roomId !== series.roomId) continue;
      const startMs = tsToMillis(data.startAt);
      const endMs = tsToMillis(data.endAt);
      if (startMs === null || endMs === null) continue;
      const start = new Date(startMs);
      const dateKey = localDateKey(start);
      if (!wanted.has(dateKey)) continue;
      // Overlap against THIS series' window on that date, so a booking earlier
      // or later the same evening in the same room is not blamed for a clash
      // it is not causing.
      const wantStart = new Date(start.getFullYear(), start.getMonth(), start.getDate(), startH, startM).getTime();
      const wantEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate(), endH, endM).getTime();
      if (endMs <= wantStart || startMs >= wantEnd) continue;
      out.push({
        id: d.id,
        dateKey,
        offeringId: typeof data.offeringId === 'string' ? data.offeringId : null,
        seriesId: typeof data.seriesId === 'string' ? data.seriesId : null,
      });
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Delete a series AND release the calendar. Removes the series doc and
 * cancels every reservation it generated that is still ahead of now (see
 * futureReservationIdsForSeries for the boundary) so the
 * slot locks are freed up. Past sessions are left in place as history.
 *
 * RETURNS THE IDS IT CANCELLED, which it used to swallow. deleteOffering
 * (lib/offerings.ts) calls this once per weekly time on the class being
 * deleted and then has to tell a Director how many sessions came off the
 * calendar in total. Counting them there would mean reading the same
 * bookings a second time; counting them here costs nothing, and a number
 * reported by the code that did the work is the only kind this repo prints.
 * Every existing caller ignores the value, which is why this widening is
 * safe rather than a behaviour change.
 *
 * AND IT REPORTS WHAT IT CANCELLED WHEN IT FAILS, not only when it succeeds.
 * The sessions come off one at a time, so a refusal on the third of five
 * leaves two genuinely off the calendar. Returning them only on the happy
 * path meant the throw carried nothing and the caller's count dropped every
 * one of them: a Director who retried after a partial run read a summary that
 * under-reported what had already come off her calendar. The ids ride on the
 * thrown error rather than in a new return shape, so the throw stays a throw
 * and every existing caller - Offering.tsx, StudioSettings.tsx, the schedule
 * edit - keeps working untouched. Read them with
 * cancelledReservationIdsFromError below; never reach for the property by
 * hand, so the two sides cannot drift.
 */
export async function deleteClassSeries(
  id: string,
  options: {
    viaServer?: boolean;
    /**
     * Bug B22, refute round 2 (write ahead): awaited before EACH removal, a
     * booking and then the weekly time itself, with the whole plan so the
     * caller can report ahead in groups. A throw stops here with nothing
     * more taken off; the ids already cancelled ride on the error as usual.
     */
    beforeRemove?: (step: { kind: 'booking' | 'series'; id: string; plan: readonly string[]; seriesId: string }) => Promise<void>;
  } = {}
): Promise<string[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const firestore = db;
  // Cancel each future session (which also releases its slot locks).
  // reservations.read is open to any signed-in user; cancel in the rules is
  // allowed to the creator or Director only.
  //
  // A MANAGER REMOVES A WEEKLY TIME THROUGH THE SERVER (bug B9, Bobbie
  // 2026-09-01: "Yes managers can update weekly Class time", removal
  // included). `viaServer` sends ONE deleteSeries call with releaseSessions:
  // the server takes off only the upcoming CLASS sessions of this one weekly
  // time, each with its room locks, and then the time itself, in the same
  // order as the Director's path (every session first, the weekly time
  // last). Reservation delete in the rules is not widened. A Manager's
  // remove always releases the whole time's upcoming sessions (B9 refute
  // rounds 3 and 4).
  const viaServer = options.viaServer === true;
  // The server lane picks its own sessions, so it cannot report each one
  // first. A caller that needs write ahead runs the Director's path.
  if (viaServer && options.beforeRemove) throw new Error('deleteClassSeries: beforeRemove needs the Director path.');
  const user = viaServer ? getAuth().currentUser : null;
  if (viaServer && !user) throw new Error('Sign in required.');
  if (user) {
    try {
      return await deleteClassSeriesViaServer(user, id, { releaseSessions: true });
    } catch (e) {
      // Stopped part way: the sessions already taken off ride on the error,
      // the same way the Director's path reports them.
      throw withCancelledReservationIds(e, releasedIdsFromError(e));
    }
  }
  const cancelled: string[] = [];
  const before = options.beforeRemove;
  try {
    const plan = await futureReservationIdsForSeries(id);
    for (const reservationId of plan) {
      if (before) await before({ kind: 'booking', id: reservationId, plan, seriesId: id });
      await cancelReservation(reservationId);
      cancelled.push(reservationId);
    }
    if (before) await before({ kind: 'series', id, plan, seriesId: id });
    await deleteDoc(doc(firestore, 'classSeries', id));
  } catch (e) {
    throw withCancelledReservationIds(e, cancelled);
  }
  return cancelled;
}

/**
 * The property name the partial-run count travels under. One spelling, read
 * and written in this file only.
 */
const CANCELLED_IDS_KEY = 'cancelledReservationIds';

/**
 * Attach the ids already cancelled to the error on its way out, WITHOUT
 * losing the original. The error's own message and any `code` on it are what
 * the screens print and branch on, so a fresh Error built here would throw
 * away the only useful part. A frozen or exotic error that refuses the
 * property still throws with everything it arrived with; it just carries no
 * count, which is exactly where this started.
 */
function withCancelledReservationIds(error: unknown, cancelled: readonly string[]): unknown {
  if (!error || typeof error !== 'object') return error;
  try {
    Object.defineProperty(error, CANCELLED_IDS_KEY, {
      value: [...cancelled],
      enumerable: true,
      configurable: true,
      writable: true,
    });
  } catch {
    // Nothing to do and nothing to say: the error itself is the message.
  }
  return error;
}

/**
 * What a failed deleteClassSeries had already taken off the calendar. Always
 * an array, so a caller can push its contents without checking anything, and
 * always strings, because an error is untrusted shape by definition.
 */
export function cancelledReservationIdsFromError(error: unknown): string[] {
  if (!error || typeof error !== 'object') return [];
  const ids = (error as Record<string, unknown>)[CANCELLED_IDS_KEY];
  if (!Array.isArray(ids)) return [];
  return ids.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/* -------------------------------------------------------------------------
 * Editing an existing weekly time
 *
 * WHAT WAS MISSING, and why this exists. classSeries had create, delete, and
 * termEnd. Nothing could change a recurring time's DAY, HOUR, ROOM or REPEAT
 * PATTERN. extendClassSeriesTerm's own comment above says as much. So
 * correcting 5:00 to 5:30, or moving a comp practice off the A weeks and onto
 * the B weeks, meant either deleting the time and retyping it or adding a
 * SECOND recurring time beside the first, which then went on booking the room
 * every week. Bobbie, 2026-09-01: "Right now if something is off you have to
 * cancel the schedule and add a new one which creates confusion. We wanted to
 * change a class from an a week to a b week and could not."
 *
 * THE RECONCILE IS THE WHOLE FEATURE. Editing the series document alone would
 * leave every already-booked future session sitting at the OLD time on the
 * OLD dates, which is the double booking this was reported for. What happens
 * instead is exactly what delete and add already do, in that order and
 * through the same functions: cancelReservation is the transaction
 * deleteClassSeries uses, and generateSessions is the booker the add form
 * uses, closures, A/B weeks and Director-cancelled days and all.
 *
 * THE REPEAT PATTERN IS THE HARDEST OF THE FIVE, and the reason the reconcile
 * matters more here than for a time change. Moving Tuesday 5:00 to Tuesday
 * 5:30 keeps the same set of DATES and only moves the hour. Moving A weeks to
 * B weeks changes WHICH DATES the class falls on at all, so a rebuild that
 * did not cancel first would leave a full term of A-week bookings standing
 * beside a full term of new B-week ones. See the A-to-B test in
 * tests/new-class-schedule.test.mjs, which drives this function end to end
 * and asserts the old dates are released and the new dates are the ones that
 * get booked.
 * ---------------------------------------------------------------------- */

/** The five fields this editor may change. Term dates have their own tooling
 *  in Studio Settings; the required-practice flag is deliberately not
 *  editable here. */
export interface ClassSeriesScheduleEdit {
  roomId: string;
  weekday: number;
  weekPattern: ClassSeriesWeekPattern;
  startTime: string;
  endTime: string;
}

export type ClassSeriesEditField = 'room' | 'day' | 'pattern' | 'start' | 'end';

export interface ClassSeriesEditPlan {
  /** Which of the five fields actually differ from the stored series. */
  changedFields: ClassSeriesEditField[];
  /**
   * True when the calendar has to be reconciled: the dates, the hour or the
   * room of every future booking would move. False means the form was
   * submitted unchanged, which is NOT an error - it is the repair path for a
   * run that died partway through booking, and it books the missing weeks
   * WITHOUT cancelling anything first. See applyClassSeriesScheduleEdit.
   */
  scheduleChanged: boolean;
}

/**
 * PURE. What an edit would actually change, decided by comparing the stored
 * series against the submitted values rather than by trusting the form to
 * know whether it was touched.
 *
 * TWO NORMALISATIONS, both load-bearing, both of which would otherwise report
 * a change on EVERY save and re-cancel a whole term for nothing:
 *   - the weekday is compared as a NUMBER, because the form hands back a
 *     string from a select and '3' !== 3
 *   - the week pattern goes through normalizeClassSeriesWeekPattern, because
 *     a series that meets every week stores no weekPattern field at all and
 *     undefined !== ''
 */
export function planClassSeriesScheduleEdit(
  before: Pick<ClassSeriesDoc, 'roomId' | 'weekday' | 'startTime' | 'endTime'> & { weekPattern?: unknown },
  next: ClassSeriesScheduleEdit
): ClassSeriesEditPlan {
  const changedFields: ClassSeriesEditField[] = [];
  if (before.roomId !== next.roomId) changedFields.push('room');
  if (Number(before.weekday) !== Number(next.weekday)) changedFields.push('day');
  if (
    normalizeClassSeriesWeekPattern(before.weekPattern)
    !== normalizeClassSeriesWeekPattern(next.weekPattern)
  ) {
    changedFields.push('pattern');
  }
  if (before.startTime !== next.startTime) changedFields.push('start');
  if (before.endTime !== next.endTime) changedFields.push('end');
  return { changedFields, scheduleChanged: changedFields.length > 0 };
}

/**
 * Write the five editable fields onto an existing series.
 *
 * A PARTIAL update, exactly like extendClassSeriesTerm: the rule validates
 * the MERGED document, so isMandatory, the term dates and both audit stamps
 * survive untouched and auditKept() passes. NO RULES CHANGE was needed for
 * any of this. weekPattern is already inside hasClassSeriesShape and already
 * constrained to '', 'A' or 'B' there, so the update lane accepts the new
 * value without a single new content byte, and the classSeries update lane
 * already admits the Director.
 */
export async function updateClassSeriesSchedule(
  seriesId: string,
  next: ClassSeriesScheduleEdit
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'classSeries', seriesId), {
    roomId: next.roomId,
    weekday: Number(next.weekday),
    weekPattern: normalizeClassSeriesWeekPattern(next.weekPattern),
    startTime: next.startTime,
    endTime: next.endTime,
  });
}

/**
 * PURE. What a Director reads when the edit refuses because the A/B calendar
 * is half filled. Same three facts #866's card gives her, in one sentence
 * she can act on: which letter is missing, that nothing was touched, and the
 * one screen that fixes it.
 */
export function scheduleEditCalendarBlockMessage(missing: readonly ('A' | 'B')[]): string {
  return (
    `The A/B calendar has no ${missing.join(' and ')} weeks listed, `
    + 'so this class would book almost nothing. Nothing was changed and not one '
    + 'booking was released. Fill in the missing weeks in Studio Settings, under '
    + '"Week A and Week B list", then save this time again.'
  );
}

/* -------------------------------------------------------------------------
 * The destination preflight
 *
 * Codex review of #949, P1, verbatim: "When a Director moves a series into a
 * room/time already occupied on some or all target dates, this loop
 * permanently deletes every old reservation before checking the
 * destination. The later generateSessions call converts each 'already
 * booked' error into a successful conflict result, so the series document
 * is updated and the editor closes even though those sessions were not
 * recreated; an entire class can disappear from family and teacher
 * calendars."
 *
 * True, and the shape of the fix follows the half-filled-calendar refusal
 * above: ASK FIRST, and if the answer is no, change nothing and release
 * nothing. The question is "which of the half-hour slot locks the new time
 * would need are held right now by a booking that is NOT one of this
 * series' own?" A slot held by one of this series' own future bookings is
 * fine, because those are the ones about to be released (the common case:
 * moving 5:00 to 5:30 in the same room, where the old 5:00-6:00 booking
 * holds the 5:30 slot). Anything else is a foreign booking and the edit
 * refuses, naming the dates, so the Director can move that booking or pick
 * another room or time and save again.
 *
 * This is a preflight, not a lock: a booking that lands between this read
 * and the rebook still shows up as a conflict in the summary, exactly as
 * before. What it removes is the case where the conflict was there all
 * along and the edit deleted a term to find out.
 * ---------------------------------------------------------------------- */

/** One half-hour slot lock, in the destination room, that is held right now. */
export interface OccupiedSlot {
  slotId: string;
  /** Whose lock it is. Empty when the doc is malformed, which counts as foreign. */
  reservationId: string;
  /** 'YYYY-MM-DD' of the planned occurrence this slot belongs to. */
  date: string;
}

/**
 * PURE. Every slot id the planned occurrences would lock in a room, each
 * mapped back to the date it belongs to, using the same slotKeysFor and
 * slotDocId the booking transaction itself uses.
 */
export function plannedSlotIds(
  roomId: string,
  occurrences: readonly Occurrence[]
): Map<string, string> {
  const out = new Map<string, string>();
  for (const occ of occurrences) {
    for (const key of slotKeysFor(occ.start, occ.end)) {
      out.set(slotDocId(roomId, key), occ.date);
    }
  }
  return out;
}

/**
 * Which of those slot locks exist, and whose they are. slots.read is open to
 * active staff. documentId() 'in' takes at most 30 ids per query, so a term
 * of thirty-odd one-hour sessions is three or four reads, not seventy.
 */
export async function occupiedSlotsForOccurrences(
  roomId: string,
  occurrences: readonly Occurrence[]
): Promise<OccupiedSlot[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const wanted = plannedSlotIds(roomId, occurrences);
  const ids = [...wanted.keys()];
  const out: OccupiedSlot[] = [];
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const snap = await getDocs(query(collection(db, 'slots'), where(documentId(), 'in', chunk)));
    for (const d of snap.docs) {
      const data = d.data() as { reservationId?: unknown };
      out.push({
        slotId: d.id,
        reservationId: typeof data.reservationId === 'string' ? data.reservationId : '',
        date: wanted.get(d.id) ?? '',
      });
    }
  }
  return out;
}

/**
 * PURE. The dates on which the destination is held by a booking that is not
 * one of this series' own. Own bookings are the ones the reconcile is about
 * to release, so their locks do not count. Sorted, deduplicated.
 */
export function foreignConflictDates(
  occupied: readonly OccupiedSlot[],
  ownReservationIds: ReadonlySet<string>
): string[] {
  const dates = new Set<string>();
  for (const slot of occupied) {
    if (slot.reservationId && ownReservationIds.has(slot.reservationId)) continue;
    dates.add(slot.date);
  }
  return [...dates].sort();
}

/** 'YYYY-MM-DD' -> 'Sep 14', for the refusal below. Falls back to the key. */
function shortDate(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return key;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * PURE. What a Director reads when the edit refuses because the new room and
 * time are already booked. Every date, in words, no "and N more": she has to
 * go and move each one.
 */
export function scheduleEditRoomConflictMessage(dates: readonly string[]): string {
  const list = dates.map(shortDate).join(', ');
  return (
    `That room is already booked at the new time on ${list}. `
    + 'Nothing was changed and not one booking was released. Move that other '
    + 'booking on the calendar, or pick a different room or time, then save this time again.'
  );
}
/**
 * The upcoming CLASS sessions of one weekly time: futureReservationIdsForSeries
 * narrowed to kind 'class' rows of the series' own class, which is exactly
 * what the server lane's cancelSeriesSession may take off and what its
 * deleteSeries counts (lib/classSeriesWrite.mjs). A Manager's remove and move
 * use this list (bug B9, refute round 2), so a rehearsal or other booking
 * that carries the series ids is left on the calendar instead of stopping
 * the run. The Director's own path is unchanged.
 */
export async function futureClassSessionIdsForSeries(
  seriesId: string,
  now: Date = new Date()
): Promise<string[]> {
  if (!db) throw new Error('Firestore not initialized.');
  const seriesSnap = await getDoc(doc(db, 'classSeries', seriesId));
  const offeringId = seriesSnap.exists() ? (seriesSnap.data() as { offeringId?: unknown }).offeringId : null;
  const snap = await getDocs(query(
    collection(db, 'reservations'),
    where('seriesId', '==', seriesId),
    where('startAt', '>=', FirestoreTimestamp.fromDate(startOfLocalDay(now)))
  ));
  const rows = snap.docs
    .filter((d) => {
      const data = d.data() as { kind?: unknown; offeringId?: unknown };
      return data.kind === 'class' && typeof offeringId === 'string' && data.offeringId === offeringId;
    })
    .map((d) => ({ id: d.id, startAt: (d.data() as { startAt?: unknown }).startAt }));
  return futureReservationIdsFromRows(rows, now.getTime());
}

export interface ApplyClassSeriesScheduleEditArgs {
  series: ClassSeriesDoc;
  next: ClassSeriesScheduleEdit;
  offeringName: string;
  /**
   * Save anyway, having been shown the shrink refusal and confirmed it. Only
   * ever set from a person answering the question, never a default: the whole
   * point is that the destructive case stops and asks.
   */
  allowShrink?: boolean;
  closures: ClosureDoc[];
  createReservation: GenerateSessionsArgs['createReservation'];
  weekCalendar?: ABWeekCalendarLookup;
  /** Regenerate from this date forward; defaults to now. Tests pin it. */
  fromDate?: Date;
  onProgress?: (completed: number, total: number) => void;
  /** Called once the old bookings are gone and rebooking is about to start. */
  onCancelled?: (count: number) => void;
  /**
   * THE THREE FIRESTORE SEAMS, defaulted to the real writes and overridable
   * ONLY so the reconcile itself can be driven end to end in a test. The
   * order of these three steps is the entire safety argument below, and an
   * argument that cannot be executed is the kind of thing this repo has
   * three times acted on and been wrong about. Nothing in the app passes
   * them; Offering.tsx calls this with the defaults.
   */
  listFutureReservationIds?: (seriesId: string, now: Date) => Promise<string[]>;
  /** The destination preflight's own read (see occupiedSlotsForOccurrences). */
  listOccupiedSlots?: (roomId: string, occurrences: readonly Occurrence[]) => Promise<OccupiedSlot[]>;
  cancelReservationById?: (reservationId: string) => Promise<void>;
  writeSeriesSchedule?: (seriesId: string, next: ClassSeriesScheduleEdit) => Promise<void>;
  /** Passed straight through to generateSessions. Tests only; see there. */
  wasCancelledByStaff?: GenerateSessionsArgs['wasCancelledByStaff'];
}

export interface ApplyClassSeriesScheduleEditResult extends GenerateSessionsResult {
  plan: ClassSeriesEditPlan;
  /** How many future bookings were released before rebooking. */
  cancelled: number;
}

/**
 * Move an existing weekly time, and take its already-booked future sessions
 * with it.
 *
 * ORDER IS THE SAFETY ARGUMENT, and each step is a primitive this file
 * already ships:
 *
 *  1. CANCEL FIRST. Every future booking of this series goes through
 *     cancelReservation, the same transaction deleteClassSeries uses, which
 *     releases the room's slot locks. Rebooking first would collide with the
 *     locks the old bookings still hold on every date whose time did not
 *     move, and generateSessions would count the whole term as conflicts.
 *  2. THEN THE DOCUMENT, so the stored series is the new truth before
 *     anything is booked against it. If the run dies after this point the
 *     series is right and some weeks are missing, which the repair path below
 *     fixes; if it died before, nothing moved at all.
 *  3. THEN REGENERATE, through generateSessions, which is the add form's own
 *     booker. Closed days are skipped, A/B weeks are honoured, and a date a
 *     Director already cancelled with "Cancel this class" is left cancelled
 *     rather than resurrected. All of that comes free with the shared
 *     function and none of it is reimplemented here.
 *
 * THE UNCHANGED-FORM CASE IS THE REPAIR PATH, not a no-op to refuse. Saving
 * without changing anything skips the cancel entirely and just runs the
 * booker, which reports an already-booked week as a conflict and leaves it
 * alone. That fills the gap left by a run that was interrupted, and it can
 * never cost a session, because nothing is released first.
 *
 * QUIET, deliberately. Families are not messaged per booking: a term is
 * around thirty of them and Bobbie's standing ruling on bulk booking is that
 * a family in many classes must not be messaged for every week of every one.
 * That means a time change is NOT announced by this function, and the screen
 * and the guide both say so in words rather than leaving it implied.
 */
export async function applyClassSeriesScheduleEdit(
  args: ApplyClassSeriesScheduleEditArgs
): Promise<ApplyClassSeriesScheduleEditResult> {
  const plan = planClassSeriesScheduleEdit(args.series, args.next);
  const weekPattern = normalizeClassSeriesWeekPattern(args.next.weekPattern);
  validateClassSeriesSchedule({
    roomId: args.next.roomId,
    weekday: Number(args.next.weekday),
    startTime: args.next.startTime,
    endTime: args.next.endTime,
    termStart: args.series.termStart,
    termEnd: args.series.termEnd,
  });

  // THE HALF-FILLED CALENDAR REFUSAL, AND IT HAS TO HAPPEN HERE, ABOVE THE
  // CANCEL LOOP. Below this point the old bookings are already gone.
  //
  // classSeriesRunsOnDate deliberately refuses to invent the missing half of
  // an A/B calendar (see its own comment), and hasConfiguredWeeks is an OR,
  // so a calendar with one letter listed switches the A/B branch on for BOTH
  // letters and then matches almost no week at all. Run through this editor
  // that is not a display problem, it is erasure: a Director who has marked
  // her first A week and then fixes a ROOM releases the class's whole term
  // and rebooks one week of it, or none, and the summary tells her so in the
  // past tense.
  //
  // BOTH LETTERS ARE CHECKED, not just the one this class ends up on. The
  // damage in the row that started this was an A-week class losing six of
  // seven sessions while its own letter was listed and the other was empty:
  // one listed week is not a filled-in calendar, and lettersMissingFromCalendar
  // can only see the empty side. A calendar with NOTHING in it is not
  // half-filled and is not refused here - that falls back to alternating from
  // each series' own term start, which is a real answer.
  //
  // Checked against the RESULTING pattern, never against "did the pattern
  // change", because a time edit on an A-week class with no A weeks listed
  // destroys exactly as much as a move does.
  if (weekPattern && args.weekCalendar) {
    const missing = lettersMissingFromCalendar(['A', 'B'], args.weekCalendar);
    if (missing.length > 0) throw new Error(scheduleEditCalendarBlockMessage(missing));
  }

  const listFuture = args.listFutureReservationIds ?? futureReservationIdsForSeries;
  const listOccupied = args.listOccupiedSlots ?? occupiedSlotsForOccurrences;
  const cancelOne = args.cancelReservationById ?? cancelReservation;
  const writeSchedule = args.writeSeriesSchedule ?? updateClassSeriesSchedule;
  const wasCancelledByStaff = args.wasCancelledByStaff ?? defaultWasCancelledByStaff;

  // ONE INSTANT for the whole reconcile, so the cancel side and the booking
  // side cannot land either side of a tick. Tests pin it through fromDate.
  const now = args.fromDate ?? new Date();

  const nextSeries: SeriesForGeneration = {
    id: args.series.id,
    offeringId: args.series.offeringId,
    roomId: args.next.roomId,
    weekday: Number(args.next.weekday),
    weekPattern,
    isMandatory: args.series.isMandatory,
    startTime: args.next.startTime,
    endTime: args.next.endTime,
    termStart: args.series.termStart,
    termEnd: args.series.termEnd,
  };

  let cancelled = 0;
  if (plan.scheduleChanged) {
    const ownReservationIds = await listFuture(args.series.id, now);

    // THE DESTINATION PREFLIGHT, and it too has to happen ABOVE THE CANCEL
    // LOOP (see the block comment on plannedSlotIds). The occurrences asked
    // about are exactly the ones generateSessions below would try to book:
    // the same fromDate, the same already-started-today filter, closed days
    // out, and dates a Director already cancelled by hand out, so a foreign
    // booking on a day this class would never book cannot refuse the edit.
    const nowMs = now.getTime();
    const planned = buildOccurrences(nextSeries, now, args.weekCalendar)
      .filter((occ) => occ.start.getTime() > nowMs)
      .filter((occ) => !closureForDate(args.closures, occ.date));
    const staffCancelled = await Promise.all(
      planned.map((occ) => wasCancelledByStaff(nextSeries.offeringId, occ.date))
    );
    const bookable = planned.filter((_occ, index) => !staffCancelled[index]);
    const conflicts = foreignConflictDates(
      await listOccupied(nextSeries.roomId, bookable),
      new Set(ownReservationIds)
    );
    if (conflicts.length > 0) throw new Error(scheduleEditRoomConflictMessage(conflicts));

    // NEVER DESTROY MORE THAN YOU REBUILD. Both numbers are already in hand:
    // ownReservationIds is what the cancel loop below is about to release, and
    // `bookable` is exactly what generateSessions will put back. See
    // scheduleEditShrinkMessage for why this is the bug that was costing
    // families their January.
    if (!args.allowShrink && ownReservationIds.length - bookable.length > SCHEDULE_EDIT_SHRINK_TOLERANCE) {
      // ONLY when weeks the class would meet carry NO LETTER YET. That is the
      // difference between the bug and an ordinary shorter schedule.
      //
      // A class legitimately meets fewer times for all sorts of reasons: it
      // moved to the letter with fewer weeks left in the term, a closed day
      // landed on it, a Director cancelled a night by hand. Refusing those
      // would make the editor nag about every honest edit and teach her to
      // click through the warning, which is worse than no warning.
      //
      // What is NOT ordinary is losing weeks because Studio Settings has not
      // been told what letter they are yet. Those sessions are recoverable the
      // moment she marks the weeks, and they are the ones that were vanishing
      // silently, so they are the only ones worth stopping the world for.
      const unmarked = await bookableUnmarkedWeekDates(nextSeries, {
        closures: args.closures,
        weekCalendar: args.weekCalendar,
        fromDate: now,
        notBefore: now,
      }, wasCancelledByStaff);
      if (unmarked.length > 0) {
        throw new Error(
          scheduleEditShrinkMessage(ownReservationIds.length, bookable.length, unmarked.length)
        );
      }
    }

    for (const reservationId of ownReservationIds) {
      await cancelOne(reservationId);
      cancelled += 1;
    }
    args.onCancelled?.(cancelled);
    await writeSchedule(args.series.id, { ...args.next, weekPattern });
  }

  const generated = await generateSessions({
    series: nextSeries,
    offeringName: args.offeringName,
    closures: args.closures,
    createReservation: args.createReservation,
    weekCalendar: args.weekCalendar,
    fromDate: now,
    notBefore: now,
    quiet: true,
    onProgress: args.onProgress,
    wasCancelledByStaff: args.wasCancelledByStaff,
  });

  return { ...generated, plan, cancelled };
}

// ---------------------------------------------------------------------------
// Occurrence generation (pure)
// ---------------------------------------------------------------------------

export interface Occurrence {
  /** 'YYYY-MM-DD' of the session, local time. */
  date: string;
  /** Local start Date for the session. */
  start: Date;
  /** Local end Date for the session. */
  end: Date;
}

/** Parse 'YYYY-MM-DD' into [year, month1-12, day] numbers. */
function parseDateKey(key: string): [number, number, number] {
  const [y, m, d] = key.split('-').map(Number);
  return [y, m, d];
}

function dateFromKey(key: string): Date {
  const [y, m, d] = parseDateKey(key);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function weekStartMonday(key: string): Date {
  const date = dateFromKey(key);
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * True when a recurring class series should run on a specific date. A weeks
 * use the Studio Settings A/B calendar when it exists. Without a configured
 * A/B calendar, A weeks are even-numbered Monday-start weeks from the term-start week;
 * B weeks are the alternating weeks. Closures are applied later by generateSessions().
 */
export function classSeriesRunsOnDate(
  series: Pick<ClassSeriesDoc, 'weekday' | 'termStart' | 'termEnd' | 'weekPattern'>,
  dateKey: string,
  weekCalendar?: ABWeekCalendarLookup
): boolean {
  if (dateKey < series.termStart || dateKey > series.termEnd) return false;
  const date = dateFromKey(dateKey);
  if (date.getDay() !== series.weekday) return false;
  const weekPattern = normalizeClassSeriesWeekPattern(series.weekPattern);
  if (!weekPattern) return true;
  // A HALF-FILLED CALENDAR MAKES A LETTER'S CLASSES DISAPPEAR, AND THAT IS
  // DELIBERATELY LEFT ALONE HERE. See StudioSettings' A/B card, which now
  // says so out loud in both its editing and saved states.
  //
  // hasConfiguredWeeks is an OR, so listing only the B dates switches this
  // branch on for A-week classes too, and weekPatternForDateKey returns null
  // for a week the calendar does not list, which never equals 'A'. Every
  // A-week class then generates no sessions. Bobbie hit exactly that,
  // urgently: "A week comp practices are missing from the calendar but B
  // week practices show up."
  //
  // A per-letter fallback to the alternation below was built for this and
  // REVERTED after review. It would have made classSeriesRunsOnDate answer
  // true for weeks the studio never listed, and this predicate does not just
  // draw a calendar: generateSessions writes real reservations through it,
  // and payroll.ts counts expectedClasses and taughtAsScheduled through it.
  // So the guess would have booked rooms and moved payroll numbers onto
  // weeks nobody chose, while weekPatternForDateKey still refused to label
  // those same weeks "A Week" anywhere in the UI.
  //
  // CLAUDE.md's golden rule decides it: a missing thing gets noticed, an
  // invented one gets believed. The honest fix for a half-filled calendar is
  // to say it is half-filled, which the settings card now does, not to
  // invent the missing half.
  if (weekCalendar?.hasConfiguredWeeks) {
    return weekPatternForDateKey(dateKey, weekCalendar) === weekPattern;
  }
  const anchorWeek = weekStartMonday(series.termStart);
  const currentWeek = weekStartMonday(dateKey);
  const weekIndex = Math.max(
    0,
    Math.round((currentWeek.getTime() - anchorWeek.getTime()) / (7 * 24 * 60 * 60 * 1000))
  );
  return weekPattern === 'A' ? weekIndex % 2 === 0 : weekIndex % 2 === 1;
}

/**
 * Walk every date from max(termStart, fromDate) to termEnd (inclusive) and
 * return the ones whose getDay() matches the series weekday, as local Date
 * objects built from the series' start/end times.
 *
 * Capped at 366 iterated days so a malformed term can't loop forever.
 * Pure + deterministic — no Firestore, no closures (the caller layers
 * closures on top in generateSessions).
 */
export function buildOccurrences(
  series: Pick<
    ClassSeriesDoc,
    'weekday' | 'weekPattern' | 'isMandatory' | 'startTime' | 'endTime' | 'termStart' | 'termEnd'
  >,
  fromDate: Date,
  weekCalendar?: ABWeekCalendarLookup
): Occurrence[] {
  const out: Occurrence[] = [];
  const fromKey = localDateKey(fromDate);
  // Start from the later of termStart and today.
  const startKey = series.termStart > fromKey ? series.termStart : fromKey;
  if (startKey > series.termEnd) return out;

  const [sy, sm, sd] = parseDateKey(startKey);
  const [ey, em, ed] = parseDateKey(series.termEnd);
  const cursor = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
  const last = new Date(ey, em - 1, ed, 0, 0, 0, 0);

  const [startH, startM] = series.startTime.split(':').map(Number);
  const [endH, endM] = series.endTime.split(':').map(Number);

  let guard = 0;
  while (cursor <= last && guard < 366) {
    guard += 1;
    const dateKey = localDateKey(cursor);
    if (classSeriesRunsOnDate(series, dateKey, weekCalendar)) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const d = cursor.getDate();
      out.push({
        date: dateKey,
        start: new Date(y, m, d, startH, startM, 0, 0),
        end: new Date(y, m, d, endH, endM, 0, 0),
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * The first possible date buildOccurrences will start from, chosen well
 * before any real term could begin. buildOccurrences walks from
 * max(termStart, fromDate), so passing an epoch this old always resolves to
 * termStart - lastBookableOccurrenceDate below needs the WHOLE term, not
 * just the occurrences still ahead of today.
 */
const TERM_EXTENSION_EPOCH = new Date(1970, 0, 1, 0, 0, 0, 0);

/**
 * The last date within a series' own term it would actually put a bookable
 * session on, closures already subtracted - the same two ingredients
 * generateSessions itself books from (buildOccurrences, then closureForDate
 * inside bookOccurrence). Built for the term-extension card's gap check (see
 * lib/termExtension's bookedShort and planTermExtension's
 * lastOccurrenceBySeriesId): a weekly class's real last session lands on its
 * own weekday, up to six days before whatever date termEnd happens to be,
 * and comparing lastBooked to termEnd directly reads that ordinary gap as a
 * permanent phantom one. Comparing lastBooked to THIS instead tells a class
 * that is genuinely done from one still missing real weeks.
 *
 * Does NOT know about a session a Director cancelled by hand with "Cancel
 * this class" - occurrenceWasCancelledByStaff (below) needs a Firestore read
 * per candidate date, and this deliberately runs off data the card already
 * has loaded (series, closures, the A/B calendar) so the gap check costs no
 * new reads. A final week cancelled that way still counts as bookable here,
 * so it can still show up as a "gap" that isn't one. That is an accepted,
 * documented limitation, not a bug: re-running is harmless, because
 * generateSessions itself checks the cancellation when it actually tries to
 * book that date and reports it back as "left alone because a Director
 * already cancelled" rather than creating a duplicate session.
 *
 * Returns '' when the series never runs at all inside its own term. That is
 * a real, checked answer, never to be confused with "not computed" - which
 * is what an absent map entry means to planTermExtension's caller.
 */
/**
 * The dates this series WOULD meet but cannot book because the week is not
 * marked A or B in Studio Settings. Bobbie, 2026-09-03, after the Edit
 * control's unchanged save and the term-fill card both reported nothing:
 * "their classes that are missing and not showing up on the calendar. I've
 * done the edit thing and it did not fix it. It's still missing."
 *
 * classSeriesRunsOnDate deliberately refuses to invent a letter for an
 * unlisted week (see its own comment), so an A-weeks-only class on a
 * calendar whose lists stop in October generates no occurrence from
 * November on, and every booker built on buildOccurrences reported that as
 * "Booked none" with no reason attached. A missing thing gets noticed, an
 * invented one gets believed: the calendar still refuses to guess, and this
 * is the noticing. PURE; every-week classes and an unconfigured calendar
 * (which alternates from term start and never has an unmarked week) give [].
 */
export function unmarkedWeekDates(
  series: Pick<ClassSeriesDoc, 'weekday' | 'weekPattern' | 'termStart' | 'termEnd'>,
  fromDate: Date,
  weekCalendar?: ABWeekCalendarLookup
): string[] {
  const out: string[] = [];
  if (!normalizeClassSeriesWeekPattern(series.weekPattern)) return out;
  if (!weekCalendar?.hasConfiguredWeeks) return out;
  const fromKey = localDateKey(fromDate);
  const startKey = series.termStart > fromKey ? series.termStart : fromKey;
  if (startKey > series.termEnd) return out;
  const [sy, sm, sd] = parseDateKey(startKey);
  const [ey, em, ed] = parseDateKey(series.termEnd);
  const cursor = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
  const last = new Date(ey, em - 1, ed, 0, 0, 0, 0);
  let guard = 0;
  while (cursor <= last && guard < 366) {
    guard += 1;
    if (cursor.getDay() === Number(series.weekday)) {
      const dateKey = localDateKey(cursor);
      if (weekPatternForDateKey(dateKey, weekCalendar) === null) out.push(dateKey);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

export function lastBookableOccurrenceDate(
  series: Pick<
    ClassSeriesDoc,
    'weekday' | 'weekPattern' | 'isMandatory' | 'startTime' | 'endTime' | 'termStart' | 'termEnd'
  >,
  closures: ClosureDoc[],
  weekCalendar?: ABWeekCalendarLookup
): string {
  const occurrences = buildOccurrences(series, TERM_EXTENSION_EPOCH, weekCalendar);
  let last = '';
  for (const occ of occurrences) {
    if (closureForDate(closures, occ.date)) continue;
    if (occ.date > last) last = occ.date;
  }
  return last;
}

// ---------------------------------------------------------------------------
// Session generation (writes via the reservation engine)
// ---------------------------------------------------------------------------

export interface GenerateSessionsResult {
  created: number;
  skippedClosure: number;
  conflicts: number;
  /**
   * A date a Director already cancelled with "Cancel this class" - kept
   * separate from skippedClosure/conflicts (and out of alreadyBookedDates /
   * closedDates) so a caller never has to describe a deliberate staff
   * cancellation as "already booked or closed", which would be false.
   */
  cancelledByStaff: number;
  /**
   * Every date that already had a session booked before this run (a re-run,
   * or a room clash with another class), in occurrence order. Nothing to do
   * here - the booking already exists.
   *
   * Kept SEPARATE from closedDates on purpose (Bobbie, screenshot report,
   * 2026-09-01: "This is unclear and we don't know what all of the canceled
   * dates are?"). The old shape folded both reasons into one skippedDates
   * list and one line of copy - "already booked or closed" - which told her
   * nothing about which dates were which, even though bookOccurrence below
   * already knows: it checks the closure FIRST and only reaches the booking
   * attempt (and a possible conflict) when the day is open. A caller that
   * wants the honest split reads these two arrays instead of one.
   */
  alreadyBookedDates: string[];
  /**
   * Every date skipped because the studio is closed that day, in occurrence
   * order. The one list worth a second look - a closed day is a real fact
   * about the calendar, not a no-op like an existing booking.
   */
  closedDates: string[];
  /** Every date skipped because a Director already cancelled that session, in occurrence order. */
  cancelledDates: string[];
  /**
   * A date where booking threw something other than "already booked" (a
   * genuine error on that one slot, not a duplicate). Every OTHER
   * occurrence still gets attempted - see the note on generateSessions
   * below - so one bad date is reported by name instead of reading as the
   * whole term failing.
   */
  failedDates: string[];
  /**
   * Dates this series would meet but which sit in a week not marked A or B
   * in Studio Settings, so nothing could be booked on them. Not a skip and
   * not a failure: the calendar refused to guess. See unmarkedWeekDates.
   */
  unmarkedWeekDates: string[];
  /**
   * Every date the room was already held at this time, BY NAME rather than
   * only counted.
   *
   * Bobbie, 2026-09-03, on Junior Hip Hop: the class page said "Not on the
   * calendar" and, underneath, "7 dates were already taken". Every one of her
   * bookable B-week Mondays was blocked and there was no way to find out by
   * WHAT. The count alone cannot say whether the slot is this class's own
   * booking, a leftover from a weekly time she removed, or a different class
   * in that room, and those three need completely different answers. A caller
   * with the dates can go and look; a caller with a number cannot.
   */
  conflictDates: string[];
}

/** Just the series fields the generator actually reads to book sessions. */
export type SeriesForGeneration = Pick<
  ClassSeriesDoc,
  | 'id'
  | 'offeringId'
  | 'roomId'
  | 'weekday'
  | 'weekPattern'
  | 'isMandatory'
  | 'startTime'
  | 'endTime'
  | 'termStart'
  | 'termEnd'
>;

export interface GenerateSessionsArgs {
  series: SeriesForGeneration;
  offeringName: string;
  closures: ClosureDoc[];
  createReservation: (input: NewReservationInput) => Promise<string>;
  /** Generate from this date forward; defaults to now. */
  fromDate?: Date;
  /**
   * An INSTANT, where fromDate is a day. Any occurrence that has already
   * STARTED by this moment is not booked at all.
   *
   * buildOccurrences works in whole day keys on purpose, so "from today"
   * always includes today's whole day. That is right for filling a term and
   * wrong for the schedule editor, which cancels first: editing Tuesday
   * 16:30 to 17:00 at 18:00 on a Tuesday would otherwise write a brand new
   * reservation for 17:00 THIS AFTERNOON, an hour that is already gone.
   *
   * Left undefined by every other lane, which keeps their day-shaped
   * behaviour exactly as it was. A dropped occurrence is not a skip and is
   * not reported as one: today's real session already happened, is still on
   * the calendar as history, and was never cancelled.
   */
  notBefore?: Date;
  weekCalendar?: ABWeekCalendarLookup;
  /**
   * Skip the per-booking notification. Set when filling a whole term at once,
   * where notifying per booking would send every family a message for every
   * week of every class their dancer is in.
   */
  quiet?: boolean;
  /**
   * Fires once up front with (0, total occurrences) before any booking
   * starts, then again every time one settles, so a caller can show live
   * "Booking week 3 of 8..." progress instead of one frozen button label
   * for however long a whole term takes.
   */
  onProgress?: (completed: number, total: number) => void;
  /**
   * SEAM FOR TESTS ONLY, defaulted to the real classPosts lookup below.
   * "A date a Director already cancelled stays cancelled" is the promise that
   * makes a rebuild safe, and it is the one promise here that used to be
   * unprovable in a node test: the real check reads Firestore directly
   * through the module's own db, which is null outside the browser, so the
   * guard silently no-opped and a test could never tell a working guarantee
   * from a missing one. Nothing in the app passes this.
   */
  wasCancelledByStaff?: (offeringId: string, dateKey: string) => Promise<boolean>;
}

type OccurrenceOutcome =
  | { kind: 'created' }
  | { kind: 'skippedClosure'; date: string }
  | { kind: 'cancelledByStaff'; date: string }
  | { kind: 'conflict'; date: string }
  | { kind: 'failed'; date: string };

/**
 * The one place the cancel-note id is spelled, so the booking lane and the
 * preview lane below cannot drift onto two different documents.
 */
function cancelClassPostId(offeringId: string, dateKey: string): string {
  return `cancelClass_${offeringId}_${dateKey}`;
}

/**
 * True when a Director already cancelled this exact session with "Cancel
 * this class" (hub/netlify/functions/cancelClass.mjs posts a note to the
 * class board at the deterministic id cancelClass_{offeringId}_{date}).
 * Re-running the generator must not silently un-cancel a session families
 * were told is off - a cancelled date is otherwise indistinguishable from
 * one that was simply never booked.
 *
 * Checked by id against classPosts rather than a new collection: Director
 * and active staff already have blanket read access to every class post
 * (canReadClassPost() in shared/firestore.rules), so this needs no new rule
 * and no new rules-suite run. An unreadable/offline check fails toward
 * booking - same "do not invent state" posture as every other guard in this
 * file - so a network hiccup here costs at worst one accidental re-book,
 * never a silent skip of a date nobody actually cancelled.
 */
async function occurrenceWasCancelledByStaff(
  firestore: Firestore,
  offeringId: string,
  dateKey: string
): Promise<boolean> {
  try {
    return await readStaffCancelled(firestore, offeringId, dateKey);
  } catch {
    return false;
  }
}

/**
 * The read both doors share. The cancellation record answers first, through
 * the one rule every reader asks (cancelledAttendance.ts, Option A, Bobbie
 * 2026-10-23): an undone cancel is not cancelled even though its class post
 * remains, and a record-only cancel is cancelled though it wrote no post. A
 * day with no record falls back to the class post, for cancels older than
 * the record.
 */
async function readStaffCancelled(firestore: Firestore, offeringId: string, dateKey: string): Promise<boolean> {
  const record = await getDoc(doc(firestore, 'classCancellations', cancelledDayKey(offeringId, dateKey)));
  if (record.exists()) return isCancellationActive(record.data() as CancellationStateInput);
  const snap = await getDoc(doc(firestore, 'classPosts', cancelClassPostId(offeringId, dateKey)));
  return snap.exists();
}

/**
 * THE ONE ANSWER TO "DID THE STUDIO ALREADY CANCEL THIS DATE", which every
 * lane now routes through: the generator books through it one occurrence at
 * a time, and cancelledOccurrenceDates below asks it in a batch for a
 * preview. Two call sites, one implementation, so a preview can never
 * promise an add the apply refuses.
 *
 * With no Firestore handle (SSR, node tests) it fails toward booking, the
 * same "do not invent state" posture every other guard in this file takes.
 */
async function defaultWasCancelledByStaff(offeringId: string, dateKey: string): Promise<boolean> {
  const firestore = db;
  return firestore ? occurrenceWasCancelledByStaff(firestore, offeringId, dateKey) : false;
}

/**
 * Which of these dates a Director already cancelled by hand, for a caller
 * that has to PREVIEW what generateSessions is going to do before it does
 * it (the A/B regenerate card). Same doc ids, same helper, same
 * fail-toward-booking posture as the check the generator itself runs one
 * occurrence at a time - deliberately one function so a preview can never
 * promise an add the apply will refuse.
 *
 * One getDoc per candidate date, in parallel. The caller only ever asks
 * about dates it is actually proposing to add, so this costs nothing on the
 * common "everything already matches" run.
 *
 * FOLDED ONTO THE GENERATOR'S OWN SEAM, which is what the note left here
 * for whoever merged second asked for. This runs through
 * defaultWasCancelledByStaff, the same function generateSessions falls back
 * to, and takes the identical injectable override, so the preview lane and
 * the booking lane cannot drift into two answers.
 */
export async function cancelledOccurrenceDates(
  offeringId: string,
  dateKeys: readonly string[],
  wasCancelledByStaff: GenerateSessionsArgs['wasCancelledByStaff'] = defaultWasCancelledByStaff
): Promise<Set<string>> {
  const out = new Set<string>();
  if (dateKeys.length === 0) return out;
  const check = wasCancelledByStaff ?? defaultWasCancelledByStaff;
  await Promise.all(
    dateKeys.map(async (dateKey) => {
      if (await check(offeringId, dateKey)) out.add(dateKey);
    })
  );
  return out;
}

/**
 * The same question as cancelledOccurrenceDates, for a caller that has to
 * SHOW the answer rather than book on it, and that must never dress an
 * unreadable check up as "nothing was cancelled".
 *
 * WHY A SECOND DOOR. occurrenceWasCancelledByStaff swallows every error and
 * returns false, on purpose: the booking lane fails toward booking, because
 * a network hiccup there costs one accidental re-book and never a silent
 * skip. A LIST is the opposite case. A card that lists a night Kim called
 * off as an ordinary gap, with a button under it, is offering to put a
 * cancelled class back in front of families and calling it a fix, and a
 * denied or offline classPosts read is exactly when that happens.
 *
 * So this one lets the read throw and leaves the caller to say what it does
 * not know. It cannot drift from the booking lane: the same readStaffCancelled
 * read, and on success the identical set. It only refuses to
 * answer, never answers differently.
 *
 * The reader is injectable for tests. With no Firestore handle it throws
 * rather than returning an empty set, for the same reason.
 */
export async function cancelledOccurrenceDatesStrict(
  offeringId: string,
  dateKeys: readonly string[],
  read?: (offeringId: string, dateKey: string) => Promise<boolean>
): Promise<Set<string>> {
  const out = new Set<string>();
  if (dateKeys.length === 0) return out;
  const check = read ?? (async (o: string, d: string) => {
    const firestore = db;
    if (!firestore) {
      throw new Error('The studio is not connected, so cancelled classes could not be checked.');
    }
    return readStaffCancelled(firestore, o, d);
  });
  await Promise.all(
    dateKeys.map(async (dateKey) => {
      if (await check(offeringId, dateKey)) out.add(dateKey);
    })
  );
  return out;
}

async function bookOccurrence(
  occ: Occurrence,
  series: SeriesForGeneration,
  reservationTitle: string,
  closures: ClosureDoc[],
  createReservation: GenerateSessionsArgs['createReservation'],
  quiet: boolean | undefined,
  wasCancelledByStaff: (offeringId: string, dateKey: string) => Promise<boolean>
): Promise<OccurrenceOutcome> {
  if (closureForDate(closures, occ.date)) {
    return { kind: 'skippedClosure', date: occ.date };
  }
  if (await wasCancelledByStaff(series.offeringId, occ.date)) {
    return { kind: 'cancelledByStaff', date: occ.date };
  }
  try {
    await createReservation({
      roomId: series.roomId,
      startAt: occ.start,
      endAt: occ.end,
      kind: 'class',
      title: reservationTitle,
      offeringId: series.offeringId,
      seriesId: series.id,
      quiet,
    });
    return { kind: 'created' };
  } catch (e) {
    // The booking guard's own refusal for a day whose cancel stands, in case
    // it was cancelled after the check above.
    if (cancelledDayFromError(e)) return { kind: 'cancelledByStaff', date: occ.date };
    const msg = e instanceof Error ? e.message : String(e);
    // "Already booked" is a re-run or a genuine room clash, not a failure -
    // every other reason (a dropped connection, a server hiccup on that one
    // write) is a real failed date, but it is THIS occurrence's problem
    // alone. It is caught here, not rethrown, specifically so it cannot
    // cancel every other week still in flight below.
    return msg.toLowerCase().includes('already booked')
      ? { kind: 'conflict', date: occ.date }
      : { kind: 'failed', date: occ.date };
  }
}

/**
 * For each occurrence of the series from now (or fromDate) through the term:
 *   - skip + count it if the studio is closed that day
 *   - otherwise book it via the shared reservation transaction. A
 *     "already booked" error (re-run, or a room clash with another class)
 *     is counted as a conflict; any other error is counted as a failed date
 *     instead of thrown.
 *
 * PARALLEL, not sequential. Each occurrence is a different calendar date, so
 * a different room+time slot-lock key: the transaction that actually guards
 * against double-booking a room is scoped to one occurrence's own slot docs
 * and never touches another occurrence's. Running eight-plus of them
 * together across a term is exactly as safe as running them one at a time,
 * just not eight-plus round trips slower, and onProgress firing as each one
 * settles is what turns a single frozen button into visible progress.
 */
export async function generateSessions(
  args: GenerateSessionsArgs
): Promise<GenerateSessionsResult> {
  const { series, offeringName, closures, createReservation, onProgress } = args;
  const notBeforeMs = args.notBefore?.getTime() ?? null;
  const occurrences = buildOccurrences(series, args.fromDate ?? new Date(), args.weekCalendar)
    .filter((occ) => notBeforeMs === null || occ.start.getTime() > notBeforeMs);
  const reservationTitle = series.isMandatory ? `Required practice: ${offeringName}` : offeringName;
  const total = occurrences.length;
  let settledCount = 0;
  onProgress?.(0, total);

  // One implementation, shared with cancelledOccurrenceDates' preview lane.
  const wasCancelledByStaff = args.wasCancelledByStaff ?? defaultWasCancelledByStaff;

  const outcomes = await Promise.allSettled(
    occurrences.map(async (occ) => {
      const outcome = await bookOccurrence(
        occ,
        series,
        reservationTitle,
        closures,
        createReservation,
        args.quiet,
        wasCancelledByStaff
      );
      settledCount += 1;
      onProgress?.(settledCount, total);
      return outcome;
    })
  );

  let created = 0;
  let skippedClosure = 0;
  let conflicts = 0;
  let cancelledByStaff = 0;
  const alreadyBookedDates: string[] = [];
  const closedDates: string[] = [];
  const cancelledDates: string[] = [];
  const failedDates: string[] = [];
  const conflictDates: string[] = [];
  for (const outcome of outcomes) {
    // bookOccurrence catches every error itself, so a rejection here would
    // only mean a bug outside that try/catch, not a booking conflict - still
    // counted as a failure, never silently dropped from the tally.
    if (outcome.status === 'rejected') {
      failedDates.push('an unknown date');
      continue;
    }
    switch (outcome.value.kind) {
      case 'created':
        created += 1;
        break;
      case 'skippedClosure':
        skippedClosure += 1;
        closedDates.push(outcome.value.date);
        break;
      case 'cancelledByStaff':
        cancelledByStaff += 1;
        cancelledDates.push(outcome.value.date);
        break;
      case 'conflict':
        conflicts += 1;
        alreadyBookedDates.push(outcome.value.date);
        conflictDates.push(outcome.value.date);
        break;
      case 'failed':
        failedDates.push(outcome.value.date);
        break;
    }
  }

  return {
    created,
    skippedClosure,
    conflicts,
    cancelledByStaff,
    alreadyBookedDates,
    closedDates,
    cancelledDates,
    failedDates,
    conflictDates,
    unmarkedWeekDates: await bookableUnmarkedWeekDates(series, args, wasCancelledByStaff),
  };
}

/**
 * The unmarked dates the booker WOULD have booked, had the week carried a
 * letter. Codex review of #988, P2: an unmarked date that is also a closed
 * day, a session a Director already cancelled, or today's already-started
 * hour would be skipped for that other reason, so telling her to mark the
 * week and run again would promise a session that still never appears. Same
 * three checks as bookOccurrence and the notBefore filter above, in the same
 * order, so the diagnostic and the booking path cannot disagree.
 */
async function bookableUnmarkedWeekDates(
  series: SeriesForGeneration,
  args: Pick<GenerateSessionsArgs, 'fromDate' | 'weekCalendar' | 'closures' | 'notBefore'>,
  wasCancelledByStaff: NonNullable<GenerateSessionsArgs['wasCancelledByStaff']>
): Promise<string[]> {
  const candidates = unmarkedWeekDates(series, args.fromDate ?? new Date(), args.weekCalendar)
    .filter((dateKey) => !closureForDate(args.closures, dateKey));
  const notBeforeMs = args.notBefore?.getTime() ?? null;
  const [startH, startM] = series.startTime.split(':').map(Number);
  const stillAhead = notBeforeMs === null
    ? candidates
    : candidates.filter((dateKey) => {
        const [y, m, d] = parseDateKey(dateKey);
        return new Date(y, m - 1, d, startH, startM, 0, 0).getTime() > notBeforeMs;
      });
  const cancelled = await Promise.all(
    stillAhead.map((dateKey) => wasCancelledByStaff(series.offeringId, dateKey))
  );
  return stillAhead.filter((_dateKey, index) => !cancelled[index]);
}
